import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Context, Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import '@rely/design-tokens/tokens.css';
import './index.css';

// Initialize i18n (must be imported before App)
import './i18n/index';

import App from './App';
import ErrorBoundary from './components/common/ErrorBoundary';
import config from './config/environment';
import { ChakraProvider, relyTheme } from './config/legacyChakraTheme';
import { POSTHOG_CONFIG, isAnalyticsEnabled } from './config/analytics';
import { captureAcquisitionSession } from './services/acquisitionSession';
import { initializeMetaPixel } from './services/metaPixel';
import { buildSentryOptions } from './config/sentry';
import { createLocalAuthSession } from './config/localAuthBypass';
import {
  getAuth0CacheLocation,
  handleAuthRedirectCallback,
} from './utils/authRedirect';

if (typeof window !== 'undefined') {
  captureAcquisitionSession();
  initializeMetaPixel();
}

// Initialize Sentry error tracking (must be before ReactDOM.createRoot)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  const environment =
    process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const release = process.env.REACT_APP_GIT_SHA || 'local';
  Sentry.init({
    ...buildSentryOptions({
      app: 'client',
      dsn: SENTRY_DSN,
      environment,
      release,
      routeProvider: () => window.location.pathname,
    }),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    // Session replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Ignore specific errors
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Network request failed',
    ],
  });
}

// Initialize PostHog analytics
if (isAnalyticsEnabled()) {
  posthog.init(POSTHOG_CONFIG.apiKey, {
    api_host: POSTHOG_CONFIG.apiHost,
    ...POSTHOG_CONFIG.options,
  });
}

// Configure React Query client with smart defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - data considered fresh
      gcTime: 1000 * 60 * 10, // 10 minutes - cache garbage collection (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: 1,
    },
  },
});

const onAuthRedirectCallback = (appState) => {
  handleAuthRedirectCallback(appState);
  captureAcquisitionSession();
};

const root = ReactDOM.createRoot(document.getElementById('root'));

const appContent = (
  <BrowserRouter>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </BrowserRouter>
);

const localAuthSession = createLocalAuthSession();
const authenticatedApp = localAuthSession ? (
  <Auth0Context.Provider value={localAuthSession}>
    {appContent}
  </Auth0Context.Provider>
) : (
  <Auth0Provider
    domain={config.auth0.domain}
    clientId={config.auth0.clientId}
    authorizationParams={{
      redirect_uri: config.auth0.redirectUri,
      audience: config.auth0.audience,
    }}
    cacheLocation={getAuth0CacheLocation()}
    useRefreshTokens={true}
    useRefreshTokensFallback={true}
    onRedirectCallback={onAuthRedirectCallback}
  >
    {appContent}
  </Auth0Provider>
);

root.render(
  <QueryClientProvider client={queryClient}>
    <ChakraProvider theme={relyTheme}>{authenticatedApp}</ChakraProvider>
  </QueryClientProvider>
);
